import { FunctionsResponse } from './functions-response';

export enum ErrorOrigin {
  Scandit, // TODO: remove completely
  API,
  Arbitrary,
}

export class AError { // extends Error {
  public static errors: {
    loginErrors: {
      invalidSessionId: number,
      invalidCredentials: number,
      alreadyLoggedIn: number,
      invalidVersion: number, // not with the 'DoNotCheckVersion' option
    },
  } = {
    loginErrors: {
      invalidSessionId: -15000,
      invalidCredentials: -15001,
      alreadyLoggedIn: -15003,
      invalidVersion: -15004,
    },
  };

  public identifier: string;

  constructor(
    public code: number,
    public message: string = '',
    public originalError: any,
    public errorOrigin: ErrorOrigin,
  ) {
    // super();
  }

  // FACTORIES

  public static fromSomeError(error: any): AError {
    if (this.hasAPIError(error)) {
      return this.fromAPIError(error);
    } else if (error instanceof AError) {
      return error;
    } else {
      const errorMessage: string = error.message || (typeof error === 'string' ? error : JSON.stringify(error));

      if (error['__zone_symbol__currentTask'] && error['__zone_symbol__currentTask'].source) {
        error = {
          source: error['__zone_symbol__currentTask'].source,
          stack: error.stack || 'not available',
        };
      }

      return new AError(0, errorMessage, error, ErrorOrigin.Arbitrary);
    }
  }

  public static fromAPIError(response: FunctionsResponse): AError {
    let message: string = response.jsonBody?.message;
    if (!message) {
      message = AError.mapHttpCodeToMessage(response.statusCode);
    }

    return new AError(response.statusCode || 0, message, response, ErrorOrigin.API);
  }

  public static wrongRequest(details: string): AError {
    return new AError(101, `Wrong Request, cancelling: ${details}`, undefined, ErrorOrigin.Arbitrary);
  }

  public static wrongResponse(response: FunctionsResponse): AError {
    return new AError(601, 'The response was not in the expected format or can not be parsed.', response, ErrorOrigin.API);
  }

  // FACTORY HELPERS

  private static hasAPIError(response: any): boolean {
    return response && response.statusCode && response.jsonBody;
  }

  private static mapHttpCodeToMessage(code: number): string {
    const map = {
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      500: 'Server Error',
    };

    return map[code];
  }

  static get signatureCanceled(): AError {
    return new AError(1100, 'Signature canceled', undefined, ErrorOrigin.Scandit);
  }

  static get locationNoPermission(): AError {
    return new AError(1400, 'No permission to retrieve location data', undefined, ErrorOrigin.Scandit);
  }

  static get locationDisabled(): AError {
    return new AError(1401, 'Retrieval of geolocation data is disabled', undefined, ErrorOrigin.Scandit);
  }

  static get locationTimeout(): AError {
    return new AError(1402, 'Timed out before acquiring a position', undefined, ErrorOrigin.Scandit);
  }

  static get getOfKeyDoesNotExist(): AError {
    const message = 'Key was not found in the object retrieved from local storage';
    return new AError(-150010, message, undefined, ErrorOrigin.Arbitrary);
  }

  static get getObjectNotAnObject(): AError {
    const message = 'Value retrieved from local storage was not an object';
    return new AError(-150011, message, undefined, ErrorOrigin.Arbitrary);
  }

  static get wrongTransportScanned(): AError {
    return new AError(800, 'Wrong transport scanned', undefined, ErrorOrigin.Arbitrary);
  }

  // OPERATIONS/CHECKS/HELPERS

  public is(error: number | AError): boolean {
    if (error instanceof AError) {
      return this.code === error.code;
    } else {
      return this.code === error;
    }
  }

  public get isNoNetwork(): boolean {
    return this.errorOrigin === ErrorOrigin.Scandit && this.code === 2;
  }

  public get isUnkown(): boolean {
    // e.g. {code:0,message:"Unknown error",name:"ScanditError"}
    return this.errorOrigin === ErrorOrigin.Scandit && this.message === 'Unknown error' && this.code === 0;
  }

  public get isTimeout(): boolean {
    // ScanditError({ code: 0, detail: "timeout", name: "ScanditError", message: "Unknown error" })
    return (this.errorOrigin === ErrorOrigin.Scandit && this.originalError && this.originalError.detail === 'timeout' && this.code === 0)
      || this.is(AError.locationTimeout);
  }

  public get isLocationTimeout(): boolean {
    return this.is(AError.locationTimeout);
  }

  public get isNetworkIssue(): boolean {
    return this.isNoNetwork || this.isTimeout;
  }

  public get isBadRequest(): boolean {
    return this.errorOrigin === ErrorOrigin.API && this.code === 400;
  }

  public get isUnauthorized(): boolean {
    return this.errorOrigin === ErrorOrigin.API && this.code === 401;
  }

  public get isForbidden(): boolean {
    return this.errorOrigin === ErrorOrigin.API && this.code === 403;
  }

  public get isNotFound(): boolean {
    return this.errorOrigin === ErrorOrigin.API && this.code === 404;
  }

  public get isServerError(): boolean {
    return this.errorOrigin === ErrorOrigin.API && this.code === 500;
  }

  public get humanMessage(): string {
    return this.message || JSON.stringify(this);
  }

  public get details(): string {
    return JSON.stringify({
      code: this.code,
      message: this.message,
      errorOrigin: this.errorOrigin,
      originalError: this.originalError,
    });
  }
}
